<template>
    <div id="prospection">
        <div class="app-container my-5">

            <div class="page-title mb-5">
                <router-link to="/providers">
                    <span class="breadcrumb-link">
                        <v-icon class="breadcrumb-link-icon">reorder</v-icon> Prestataires
                    </span>
                </router-link>
                <v-icon>keyboard_arrow_right</v-icon>
                <span class="breadcrumb-item">
                    <v-icon class="breadcrumb-item-icon">mdi-plus-circle</v-icon> Création d'une fiche
                </span>
            </div>
            <v-alert text class="mb-5" type="info">
                <div>La création de fiche vous permet de rechercher un établissement, et de générer des données,
                    récupérées sur ses différentes pages Internet.
                </div>
                <div>
                    <span>Une fois les informations générées, vous pourrez créer la fiche sur Dahub</span>
                    <span v-if="account.tis_type.code === 'APIDAE'"> et Apidae</span>
                    <span>.</span>
                </div>
                <div>Attention, les informations ne peuvent être générées qu’à condition qu’il existe des données sur
                    Internet pour cet établissement.
                </div>
            </v-alert>

            <v-alert type="warning" v-if="account.member_id === null && account.tis_type.code !== 'DAHUB_QUEBEC'">
                Votre membre id n'est pas configuré sur Dahub. Merci de contacter le support.
            </v-alert>

            <div v-else>
                <div class="w-100 mt-5 d-flex">
                    <div class="d-flex w-100">
                        <places></places>
                    </div>

                    <div class="position-relative">
                        <div id="geocoder" class="mr-4"
                             :class="{ 'disabled': (placeId !== null && existCommune) || searchGoogleSource }">
                        </div>

                        <v-progress-circular class="progressbar ml-2" indeterminate size="20" v-if="searchGoogleSource"
                                             color="#808080FF" width="2">
                        </v-progress-circular>
                    </div>

                    <div>
                        <v-btn class="default-button" @click="searchLocalTuples" :disabled="!enableSearchButton">
                            <span>Rechercher</span>
                        </v-btn>
                    </div>

                    <div>
                        <v-tooltip slot="append-outer" bottom>
                            <template v-slot:activator="{ on }">
                                <v-btn text color="primary" v-on="on" @click="cancelSearch" class="ml-4 text-none"
                                       style="width: 40px !important; min-width: 40px !important; border: solid #1976d2 1px">
                                    <svg viewBox="0 0 24 24" height="17" width="17" aria-hidden="true" focusable="false"
                                         fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor"
                                         stroke-linecap="round" stroke-linejoin="round"
                                         class="StyledIconBase-ea9ulj-0 bWRyML" style="stroke-width: 2;">
                                        <line x1="20" x2="4" y1="4" y2="20"></line>
                                        <line x1="4" x2="20" y1="4" y2="20"></line>
                                    </svg>
                                </v-btn>
                            </template>
                            <span>Stopper la recherche</span>
                        </v-tooltip>
                    </div>
                </div>

                <div v-if="currentStep !== 0" class="mb-5">
                    <v-stepper v-model="currentStep">
                        <v-stepper-header>
                            <v-stepper-step step="1" :complete="currentStep > 1">
                                Dédoublonnage
                            </v-stepper-step>

                            <v-stepper-step step="2" :complete="currentStep > 2">
                                Recherche des sources
                            </v-stepper-step>
                        </v-stepper-header>

                        <v-stepper-items>
                            <v-stepper-content step="1">
                                <v-card class="p-4" v-if="!searchedLocalTuples">
                                    <div class="mt-auto mb-auto d-flex" v-if="searchingLocalTuples">
                                        <div style="font-size: 19px">Nous recherchons l'existence de doublons sur Dahub
                                        </div>
                                        <div class="dot-pulse mt-auto mb-auto" style="margin-left: 35px"></div>
                                    </div>

                                    <div class="mt-auto mb-auto" v-if="localTuples.length">
                                        <div style="font-size: 19px">Nous avons trouvé des fiches sur Dahub qui portent
                                            un nom très similaire à votre recherche :
                                        </div>
                                        <ul class="mt-3">
                                            <li v-for="tuple in localTuples">
                                                <div class="d-flex">
                                                    <div class="mt-auto mb-auto">{{ tuple.last_version.name }}</div>
                                                    <div class="mt-auto mb-auto"
                                                         v-if="tuple.last_version.localisation !== null">
                                                        <span class="ml-2 mr-2">–</span>
                                                        <span>{{ tuple.last_version.localisation.address }}</span>
                                                        <span v-if="tuple.last_version.localisation.commune !== null">,
                                                            {{ tuple.last_version.localisation.commune.name }}
                                                        </span>
                                                    </div>
                                                    <v-btn class="ml-2" icon target="_blank"
                                                           :href="'/summary/' + encodeUri(tuple.id)">
                                                        <v-icon>open_in_new</v-icon>
                                                    </v-btn>
                                                </div>
                                            </li>
                                        </ul>

                                        <div class="d-flex justify-content-end gap-10 w-100 mt-5">
                                            <v-btn color="primary" @click="keepSearch">Ignorer</v-btn>
                                            <v-btn color="error" @click="cancelSearch">Stopper la recherche</v-btn>
                                        </div>
                                    </div>
                                </v-card>

                                <v-card class="p-4" v-else>
                                    <div class="mt-auto mb-auto d-flex" v-if="searchingApidaeTuples">
                                        <div style="font-size: 19px">Nous recherchons l'existence de doublons sur Apidae
                                        </div>
                                        <div class="dot-pulse mt-auto mb-auto" style="margin-left: 35px"></div>
                                    </div>

                                    <div class="mt-auto mb-auto" v-if="apidaeTuples.length">
                                        <div style="font-size: 19px">Nous avons trouvé des fiches sur Apidae qui portent
                                            un nom très similaire à votre recherche :
                                        </div>
                                        <ul class="mt-3">
                                            <li v-for="tuple in apidaeTuples">
                                                <div class="d-flex">
                                                    <div class="mt-auto mb-auto">{{ tuple.nom.libelleFr }}</div>
                                                    <v-btn class="ml-2" icon target="_blank"
                                                           :href="'https://base.apidae-tourisme.com/gerer/objet-touristique/' + tuple.id + '/consulter/'">
                                                        <v-icon>open_in_new</v-icon>
                                                    </v-btn>
                                                </div>
                                            </li>
                                        </ul>

                                        <div class="d-flex justify-content-end w-100 mt-5">
                                            <v-btn color="primary" @click="searchSources">Ignorer</v-btn>
                                            <v-btn color="error" @click="cancelSearch">Stopper la recherche</v-btn>
                                        </div>
                                    </div>
                                </v-card>
                            </v-stepper-content>

                            <v-stepper-content step="2">
                                <div class="d-flex sources">
                                    <div v-for="source in sources" class="w-100">
                                        <v-card :class="'card-background-' + source.state" class="m-2 p-3">
                                            <div class="d-flex w-100">
                                                <v-tooltip bottom>
                                                    <template v-slot:activator="{ on }">
                                                        <v-img class="mr-3" v-on="on"
                                                               :src="'/images/logo-' + source.name + '.jpg'"
                                                               :alt="source.name">
                                                        </v-img>
                                                    </template>
                                                    <span>{{ source.name }}</span>
                                                </v-tooltip>

                                                <div class="mt-auto mb-auto d-flex" v-if="source.state === 'SEARCHING'">
                                                    <div>Recherche en cours</div>
                                                </div>

                                                <div class="mt-auto mb-auto d-flex"
                                                     v-else-if="source.state === 'NOT_FOUND'">
                                                    <div class="font-weight-bold" style="color: #d94d4d">Page non
                                                        trouvée
                                                    </div>
                                                </div>

                                                <div class="mt-auto mb-auto d-flex justify-content-around"
                                                     v-else-if="source.state === 'FOUND'">
                                                    <div class="font-weight-bold mt-auto mb-auto"
                                                         style="color: rgb(108 171 88);">Page trouvée
                                                    </div>
                                                </div>

                                                <div class="mt-auto mb-auto ml-auto" v-if="source.state === 'FOUND'">
                                                    <v-btn icon target="_blank" :href="source.providerData.source.url">
                                                        <v-icon color="primary">open_in_new</v-icon>
                                                    </v-btn>
                                                </div>
                                            </div>
                                        </v-card>
                                    </div>
                                </div>

                                <div v-if="progressBar !== null && progressBar < 100" class="ml-2 mr-2 mt-5">
                                    <v-progress-linear v-model="progressBar" height="25" class="mb-5" rounded
                                                       style="color: white" stream striped reactive>
                                        <strong>{{ Math.ceil(progressBar) }}%</strong>
                                    </v-progress-linear>

                                    <div class="position-relative" style="height: 70px; margin-top: -20px">
                                        <div class="progress-msg text-center w-100" v-if="searchMessage !== null">
                                            {{ searchMessage }}
                                        </div>
                                    </div>
                                </div>

                                <div class="w-100" v-if="finished">
                                    <div style="min-height: calc(100vh - 64px)" id="webform">
                                        <div class="container" style="border: none; padding-top: 30px">
                                            <div class="d-flex w-100">
                                                <div style="flex: 1 1 0">
                                                    <general :provider="provider"></general>
                                                    <localisation :provider="provider" v-if="provider.localisation !== undefined &&
                                                    provider.localisation.address !== undefined">
                                                    </localisation>
                                                </div>
                                                <div v-if="marker !== null" style="flex: 1 1 0" class="ml-5">
                                                    <l-map style="height: 350px" :zoom="16" :center="marker">
                                                        <l-tile-layer :url="url" :attribution="attribution"/>
                                                        <l-marker :lat-lng="marker" :draggable="true"
                                                                  @dragend="updateLatLng"></l-marker>
                                                    </l-map>
                                                </div>
                                            </div>

                                            <content :provider="provider" v-if="provider.contents !== undefined">
                                            </content>
                                            <communication :provider="provider"
                                                           v-if="provider.communications !== undefined"
                                                           :fb-page-url="fbPageUrl"
                                                           :trip-page-url="tripPageUrl">
                                            </communication>
                                            <opening-period :provider="provider" v-if="provider.opening_periods !== undefined &&
                                            provider.opening_periods.length">
                                            </opening-period>

                                            <div style="margin-top: 30px" class="w-100 d-flex justify-content-between">
                                                <v-btn x-large
                                                       color="primary"
                                                       outlined
                                                       class="text-none create-btn"
                                                       @click="createProvider"
                                                       :disabled="!finished ||
                                                                  provider.category === null ||
                                                                  (provider.category &&
                                                                  provider.category.name === 'UNDEFINED')">
                                                    <v-icon color="primary" class="mr-3">edit</v-icon>
                                                    Créer la fiche sur Dahub
                                                </v-btn>

                                                <div class="mt-auto mb-auto"
                                                     v-if="finished &&
                                                           provider.category &&
                                                           provider.category.name !== 'UNDEFINED' &&
                                                           account.tis_type.code === 'APIDAE'">
                                                    <b>OU</b></div>

                                                <v-btn v-if="account.tis_type.code === 'APIDAE'"
                                                       x-large
                                                       color="primary"
                                                       outlined
                                                       class="text-none"
                                                       @click="writeApidae"
                                                       :disabled="!finished ||
                                                    (provider.category &&
                                                     provider.category.name === 'UNDEFINED')">
                                                    <v-icon color="primary" class="mr-3">edit</v-icon>
                                                    Créer la fiche sur Dahub + Apidae
                                                </v-btn>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </v-stepper-content>
                        </v-stepper-items>
                    </v-stepper>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import _ from "lodash"
import General from "./tabulations/General"
import Content from "./tabulations/Content"
import Localisation from "./tabulations/Localisation"
import Communication from "./tabulations/Communication"
import OpeningPeriod from "./tabulations/OpeningPeriod"
import {mapState} from "vuex"
import Stepper from "../../tools/Stepper"
import Places from "../../tools/Places"
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
import {latLng} from "leaflet"
import axios from 'axios'

export default {
    name: "CreateProvider",
    components: {Places, Stepper, OpeningPeriod, Communication, Localisation, Content, General},
    data() {
        return {
            search: null,
            city: null,
            cityId: null,
            loadingSources: false,
            sources: [],
            loadingCommunes: false,
            searchCommunes: null,
            communes: [],
            fetchedSources: 0,
            finished: false,
            provider: {},
            dialog: false,
            currentStep: 0,
            localTuples: [],
            apidaeTuples: [],
            searchingLocalTuples: false,
            searchingApidaeTuples: false,
            searchedLocalTuples: false,
            placeId: null,
            googleSource: null,
            searchGoogleSource: false,
            providerName: null,
            existCommune: false,
            progressBar: null,
            progressInterval: null,
            searchMessage: null,
            timeSearch: 0,
            tripPageUrl: null,
            fbPageUrl: null,
            lat: null,
            lng: null,
            geocorder: null,
            marker: null,
            url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
            attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
            requests: [],
            cancelled: false
        }
    },
    methods: {
        addRequest(name) {
            let axiosSource = axios.CancelToken.source()
            let request = {cancel: axiosSource.cancel, name: name, token: axiosSource.token}

            this.requests.push(request)

            return request
        },
        async searchLocalTuples() {
            this.cancelled = false
            this.requests = []

            let payload = {
                search: this.providerName,
                city: this.city,
                cityId: this.cityId,
                lat: this.lat,
                lng: this.lng
            }

            this.currentStep = 1
            let startTime = performance.now()
            this.searchingLocalTuples = true
            this.searchedLocalTuples = false

            let request = this.addRequest('search-local-tuples')

            try {
                let res = await this.$http.post('auth/providers/tuples/local', payload, {cancelToken: request.token})

                await this.addDelay(startTime, 2000)

                if (!this.cancelled) {
                    this.localTuples = res.body.data.tuples
                    this.searchingLocalTuples = false

                    if (this.localTuples.length === 0) {
                        if (this.account.tis_type.code === 'APIDAE') {
                            this.searchApidaeTuples()
                        } else {
                            this.searchSources()
                        }
                    }
                }
            } catch (error) {
                this.searchingLocalTuples = false
            }
        },
        async searchApidaeTuples() {
            let payload = {
                search: this.providerName,
                city: this.city,
                cityId: this.cityId,
                lat: this.lat,
                lng: this.lng
            }

            let request = this.addRequest('search-apidae-tuples')

            this.searchingApidaeTuples = true
            this.searchedLocalTuples = true
            let startTime = performance.now()

            try {
                let res = await this.$http.post('auth/providers/tuples/apidae', payload, {cancelToken: request.token})

                await this.addDelay(startTime, 2000)

                if (!this.cancelled) {
                    this.apidaeTuples = res.body.data.tuples
                    this.searchingApidaeTuples = false

                    if (this.apidaeTuples.length === 0) {
                        this.searchSources()
                    }
                }
            } catch (error) {
                this.searchingApidaeTuples = false
            }
        },
        // On veut parfois ajouter des délais pour que l'utilisateur ai le temps de lire. On se base sur le temps
        // d'exécution pour en ajouter un peu
        async addDelay(startTime, neededDelay) {
            let endTime = performance.now()
            let delay = endTime - startTime

            if (delay < neededDelay) {
                await new Promise(r => setTimeout(r, neededDelay - delay))
            }
        },
        encodeUri(id) {
            return btoa(id)
        },
        searchSources() {
            let payload = {
                search: this.providerName,
                city: this.city,
                cityId: this.cityId
            }

            this.loadingSources = true
            this.finished = false
            this.currentStep = 2
            this.searchMessage = null
            this.timeSearch = null
            this.apidaeTuples = []

            this.sources = [
                {name: 'GOOGLE', state: 'SEARCHING', providerData: null, label: 'Google'},
                {name: 'FACEBOOK', state: 'SEARCHING', providerData: null, label: 'Facebook'},
                {name: 'TRIPADVISOR', state: 'SEARCHING', providerData: null, label: 'Tripadvisor'},
                {name: 'YELP', state: 'SEARCHING', providerData: null, label: 'Yelp'}
            ]

            this.progressBar = 0

            this.progressInterval = setInterval(() => {
                this.progressBar += (0.1 * (10 / 6)) // le timeout est de 1min sur la recherche de sources

                if (this.progressBar < 20) {
                    this.searchMessage = "Recherche de la présence en ligne de la structure"
                } else if (this.progressBar >= 20 && this.progressBar < 40) {
                    this.searchMessage = "Collecte des données en cours..."
                } else if (this.progressBar >= 40 && this.progressBar < 70) {
                    this.searchMessage = "Collecte des données un peu plus longue que prévu..."
                } else {
                    this.searchMessage = "On y est presque 🙂"
                }
            }, 100)

            let startTime = performance.now()

            let request = this.addRequest('search-sources')

            this.$http.post('online-presence/search', payload, {cancelToken: request.token}).then(res => {
                let fetchedSources = res.body.data.sources
                let endTime = performance.now()

                this.timeSearch = parseInt(endTime - startTime)

                this.sources.forEach(source => {
                    let exist = fetchedSources.find(fetchedSource => fetchedSource.type === source.name)

                    if (!exist) {
                        source.state = 'NOT_FOUND'

                        this.incrementSourcesCount()
                    } else {
                        source.state = 'FOUND'

                        let com = {
                            value: exist.url,
                            type: {
                                name: source.name,
                                label: source.label
                            }
                        }

                        source.providerData = {source: exist, communications: [com]}

                        this.searchProviderData(exist, source)
                    }
                })
            }).catch(() => {
                // on a rien à faire mais bon est-ce qu'on veut vraiment des logs sentry ? 🤔
            })
        },
        searchProviderData(fetchedSource, source) {
            let payload = {
                source: fetchedSource
            }

            // on a calculé le temps pour rechercher les sources, on le déduit du timeout global
            let timeout = 60000 - this.timeSearch

            let request = this.addRequest('search-' + fetchedSource.type)
            let options = {cancelToken: request.token, timeout: timeout}

            this.$http.post('online-presence/search/sources', payload, options).then(res => {
                let providerData = res.body.data.providerData

                if (providerData !== null) {
                    source.providerData = res.body.data.providerData
                }

                this.incrementSourcesCount()
            }).catch(err => {
                if (!this.cancelled) {
                    this.incrementSourcesCount()
                }
            })
        },
        incrementSourcesCount() {
            this.fetchedSources++

            if (this.fetchedSources >= 4 && !this.cancelled) {
                this.loadingSources = false
                this.fetchedSources = 0

                this.finished = true

                clearInterval(this.progressInterval)

                this.progressBar = null

                this.initProvider()
            }
        },
        initProvider() {
            this.tripPageUrl = null
            this.fbPageUrl = null

            let provider = this.initWithGoogle()

            if (provider.name === undefined) {
                provider.name = this.providerName
            }

            if (provider.category === undefined) {
                provider.category = this.categories.find(category => category.name === 'UNDEFINED')
            }

            if (provider.communications === undefined) {
                provider.communications = []
            }

            if (provider.contents === undefined) {
                provider.contents = []
            }

            if (provider.localisation === undefined) {
                provider.localisation = {}
            }

            this.sources.forEach(source => {
                if (source.providerData !== null) {
                    this.setCommunications(source, provider)
                    this.setContents(source, provider)
                    this.setLocalisation(source, provider)
                }
            })

            if (provider.communications.length === 0) {
                delete provider.communications
            }

            if (provider.contents.length === 0) {
                delete provider.contents
            }

            if (provider.localisation.address === null) {
                delete provider.localisation.address
            }

            if (provider.localisation.commune === null) {
                delete provider.localisation.commune
            }

            if (provider.localisation.latitude === null) {
                delete provider.localisation.latitude
            }

            if (provider.localisation.longitude === null) {
                delete provider.localisation.longitude
            }

            if (provider.category === null) {
                provider.category = this.categories.find(category => category.name === 'UNDEFINED')
            }

            this.setDefaultEmail(provider)

            this.provider = provider
            this.dialog = true
        },
        initWithGoogle() {
            let provider = {}
            let googleSource = this.sources.find(source => source.name === 'GOOGLE')

            if (googleSource !== undefined && googleSource !== null && googleSource.providerData !== null) {
                provider = JSON.parse(JSON.stringify(googleSource.providerData))

                delete provider.source
                delete provider.references
                delete provider.opening_periods
                delete provider.medias

                if (provider.opening_days !== undefined && provider.opening_days.length) {
                    let period = {
                        valid_from: this.$moment().startOf('year').format('YYYY-MM-DD'),
                        valid_through: this.$moment().endOf('year').format('YYYY-MM-DD'),
                        h24: provider.h24,
                        opening_days: provider.opening_days
                    }

                    provider.opening_periods = [period]

                    delete provider.opening_days
                    delete provider.h24
                }
            }

            if (provider.localisation !== null && provider.localisation !== undefined &&
                provider.localisation.latitude !== null && provider.localisation.latitude !== undefined) {

                this.marker = latLng(provider.localisation.latitude, provider.localisation.longitude)
            }

            return provider
        },
        setDefaultEmail(provider) {
            let hasEmail = provider.communications.find(com => com.type.name === 'EMAIL')
            let tripSource = this.sources.find(source => source.name === 'TRIPADVISOR')
            let fbSource = this.sources.find(source => source.name === 'FACEBOOK')

            if (tripSource.providerData !== null && tripSource.providerData.source !== undefined) {
                this.tripPageUrl = tripSource.providerData.source.url
            }

            if (fbSource.providerData !== null && fbSource.providerData.source !== undefined) {
                this.fbPageUrl = fbSource.providerData.source.url
            }

            if (hasEmail === undefined && (this.tripPageUrl || this.fbPageUrl)) {
                let emailCom = {
                    value: null,
                    type: {
                        name: 'EMAIL',
                        label: 'Email'
                    }
                }

                provider.communications.push(emailCom)
            }
        },
        setCommunications(source, provider) {
            if (source.providerData.communications !== undefined) {
                source.providerData.communications.forEach(communication => {
                    let exist = provider.communications.find(com => com.type.name === communication.type.name)

                    if (!exist) {
                        provider.communications.push(communication)
                    }
                })
            }
        },
        setContents(source, provider) {
            if (source.providerData.contents !== undefined) {
                source.providerData.contents.forEach(content => {
                    let exist = provider.contents.find(ct => ct.language.code === content.language.code)

                    if (!exist) {
                        provider.contents.push(content)
                    }
                })
            }
        },
        setLocalisation(source, provider) {
            let localisation = source.providerData.localisation

            if (localisation !== undefined) {
                if (provider.localisation.address === null && localisation.address !== undefined && localisation.address !== null) {
                    provider.localisation.address = localisation.address
                }

                if(provider.localisation.postal_code === null && localisation.postal_code !== undefined &&
                  localisation.postal_code !== null) {
                    provider.localisation.postal_code = localisation.postal_code
                }

                if (provider.localisation.commune === null && localisation.commune !== undefined && localisation.commune !== null) {
                    provider.localisation.commune = localisation.commune
                }

                if (provider.localisation.latitude === null && localisation.latitude !== undefined && localisation.latitude !== null) {
                    provider.localisation.latitude = localisation.latitude
                    provider.localisation.longitude = localisation.longitude
                }
            }
        },
        async createProvider(redirect = true) {
            let emailCom = this.provider.communications !== undefined ?
                this.provider.communications.find(com => com.type.name === 'EMAIL') : null

            let email = emailCom !== undefined && emailCom !== null ? emailCom.value : null

            if (emailCom !== null && (email === null || email === '')) {
                this.provider.communications.forEach((com, index) => {
                    if (com.type.name === 'EMAIL') {
                        this.provider.communications.splice(index, 1)
                    }
                })
            }

            let payload = {
                provider: this.provider,
                userId: this.user.id,
                email: email,
                withMessage: redirect
            }

            this.$global.loading = true

            let res = await this.$http.post('online-presence/create-provider', payload)

            this.$global.loading = false

            let providerId = res.body.data.provider_id

            if (redirect) {
                let hash = btoa(providerId)

                setTimeout(() => {
                    this.$router.push('/summary/' + hash)
                }, 1000)
            }

            return providerId
        },
        async writeApidae() {
            let providerId = await this.createProvider(false)

            let payload = {
                providerId: providerId,
                userId: this.user.id
            }

            this.$global.loading = true

            this.$http.post('online-presence/write-apidae', payload).then(res => {
                this.$global.loading = false

                if (res.body.success) {
                    let hash = btoa(providerId)

                    setTimeout(() => {
                        this.$router.push('/summary/' + hash)
                    }, 3000)
                } else {
                    this.$http.delete('auth/providers/' + providerId + '/false')
                }
            })
        },
        cancelSearch() {
            this.cancelled = true

            this.requests.forEach(request => {
                request.cancel()
            })

            this.requests = []
            this.currentStep = 0
            this.localTuples = []
            this.apidaeTuples = []
            this.sources = []
            this.communes = []
            this.existCommune = false
            this.search = null
            this.providerName = null
            this.city = null
            this.cityId = null

            if (this.progressInterval !== null) {
                clearInterval(this.progressInterval)
            }

            this.progressInterval = null
            this.progressBar = null
            this.fetchedSources = 0

            this.$global.$emit('clear-places')

            this.geocorder.setInput('')
            this.clearMapboxSuggestions()
        },
        clearMapboxSuggestions() {
            let wrapper = document.getElementsByClassName('suggestions-wrapper')

            if (wrapper !== undefined) {
                wrapper = wrapper[0]

                let ul = wrapper.getElementsByTagName('ul')

                if (ul !== undefined) {
                    ul = ul[0]

                    while (ul.firstChild) {
                        ul.removeChild(ul.firstChild)
                    }
                }
            }
        },
        keepSearch() {
            this.localTuples = []

            if (this.account.tis_type.code === 'APIDAE') {
                this.searchApidaeTuples()
            } else {
                this.searchSources()
            }
        },
        fetchGoogleProvider() {
            this.cancelled = false
            this.localTuples = []
            this.apidaeTuples = []
            this.sources = []

            this.finished = false
            this.searchGoogleSource = true
            this.existCommune = false
            this.currentStep = 0

            this.geocorder.setInput('')
            this.geocorder.setAutocomplete(false)

            let request = this.addRequest('fetch-google-provider')

            return this.$http.get('online-presence/google/' + this.placeId, {cancelToken: request.token}).then(res => {
                let source = res.body.data.source

                this.communes = []
                this.search = source.name
                this.googleSource = source

                if (source.localisation.commune !== null) {
                    this.cityId = source.localisation.commune.id

                    this.communes.push(this.city)
                    this.geocorder.setInput(source.localisation.commune.name)

                    this.existCommune = true

                    this.searchLocalTuples()
                } else {
                    this.cityId = null
                }

                this.searchGoogleSource = false
            }).catch(() => {
                this.searchGoogleSource = false
            })
        },
        getCommunes: _.debounce(function () {
            this.loadingCommunes = true

            let payload = {
                search: this.searchCommunes
            }

            this.$http.post('resource/communes', payload).then(res => {
                this.communes = res.body.data.communes

                this.loadingCommunes = false
            })
        }, 500),
        loadMapBoxGeocoder() {
            let geocoder = new MapboxGeocoder({
                accessToken: "pk.eyJ1IjoibG1pbGxvbiIsImEiOiJja3d3NW94MnMwMGZpMndxdHB0amFvYzdlIn0.VhcuyFFqm0p9clPLE0x5kw",
                types: 'place,locality,postcode',
                limit: 5,
                language: 'fr',
                countries: this.country,
                fuzzyMatch: true,
                autocomplete: true,
                placeholder: this.account.tis_type.code === "DAHUB_QUEBEC" ? 'Municipalité' : 'Commune',

                render(item) {
                    return "<div style='color: black; font-weight: bold'>" + item.place_name + "</div>"
                }
            })

            geocoder.addTo('#geocoder')

            geocoder.on('result', (event) => {
                let result = event.result !== undefined ? event.result : event

                this.lat = result.geometry.coordinates[1]
                this.lng = result.geometry.coordinates[0]
                this.city = result.text
            })

            geocoder.on('loading', (event) => {
                this.city = event.query
            })

            this.geocorder = geocoder
        },
        updateLatLng(event) {
            let marker = event.target
            let latLng = marker.getLatLng()

            this.provider.localisation.latitude = latLng.lat
            this.provider.localisation.longitude = latLng.lng
        }
    },
    watch: {
        searchCommunes() {
            this.searchCommunes = this.searchCommunes.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase()

            this.getCommunes()
        }
    },
    computed: {
        ...mapState('account', ['user', 'account']),
        ...mapState('resource', ['categories']),
        enableSearchButton() {
            return this.providerName !== null &&
                this.providerName !== '' &&
                (this.cityId !== null || this.city !== null) &&
                !this.loadingSources &&
                !this.searchGoogleSource &&
                !this.searchingLocalTuples &&
                !this.searchingApidaeTuples &&
                !this.localTuples.length &&
                !this.apidaeTuples.length
        },
        country() {
            return this.account.tis_type.code === 'DAHUB_QUEBEC' ? 'ca' : 'fr'
        }
    },
    mounted() {
        this.loadMapBoxGeocoder()
    },
    created() {
        this.$store.dispatch('resource/fetchAllCategories')

        if (this.account.tis_type.code == 'DAHUB_QUEBEC') {
            this.$store.dispatch('resource/fetchQuebecCategories')
        }

        this.$global.$on('google-search', (location) => {
            if (location !== undefined && location !== null && typeof location === 'object') {
                this.placeId = location.place_id
                this.providerName = location.structured_formatting.main_text

                this.fetchGoogleProvider()
            } else {
                this.placeId = null
                this.providerName = location
            }
        })
    },
    beforeDestroy() {
        this.$global.$off('google-search')
    }
}
</script>